<script setup>
import { ref } from 'vue';
import { modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import dayjs from 'dayjs';
import ITeams from '@components/ITeams/ITeams.vue';
import ITicket from '@components/ITicket/ITicket.vue';
import IRefund from '@components/IRefund/IRefund.vue';
import IPackageEvent from '@components/IPackageEvent/IPackageEvent.vue';
import IPackageEventName from '@components/IPackageEventName/IPackageEventName.vue';
import ILink from '@components/ILink/ILink.vue';
import ExtraInfo from '@components/ExtraInfo.vue';
import useCartStore from '@vueApp/store/modules/cart';
import { utils } from '@frontend/web/src/js/utils';
import 'dayjs/locale/ru';

translator.getLanguages();

const lang = ref($('html').attr('lang') || 'ru');
dayjs.locale(lang.value);

const cartStore = useCartStore();
const props = defineProps({
  itemCase: {
    type: Object,
    required: true,
  },
  trashButton: {
    type: Boolean,
    default: true,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  showTeams: {
    type: Boolean,
    default: true,
  },
});

const emits = defineEmits(['removeEvent']);

const eventFormattedDate = ref(
  props.itemCase.startTimestampStr,
);

function removeTicket(ticket, eventId, type) {
  const adultTicketsCount = props.itemCase.tickets.reduce((
    ticketsCount,
    currentTicket,
  ) => (currentTicket.loyaltyCategoryId === null ? ticketsCount + 1 : ticketsCount), 0);

  const hasChildTicket = props.itemCase.tickets.some(
    (currentTicket) => currentTicket.loyaltyCategoryId === 1,
  );

  if (hasChildTicket && adultTicketsCount <= 1) {
    modal.error(translator.t('order', 'Need at least one adult ticket!'));
    return;
  }

  if (type === 'service_simple') {
    cartStore.removeServiceTicket(ticket, eventId);
  } else {
    cartStore.removeEventTicket(ticket, eventId);
  }
}

function removeEvent(eventId, type, countTickets) {
  if (type === 'service_simple') {
    cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
  } else {
    cartStore.removeEvent(eventId, props.itemCase.orderId);
  }
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}

const isPackageEventsShow = ref(false);
</script>

<template>
  <div
    class="i-order-event-item"
    :class="isHistory ? 'i-order-event-item__history' : ''"
  >
    <div v-if="showTeams" class="i-order-event-item__top-wrapper">
      <a class="i-order-event-item__team-link" :href="itemCase.pageLink">
        <div class="i-order-event-item__top-left">
          <template v-if="itemCase.type == 'vagrant'">
            <div class="i-order-event-item__name">
              <IPackageEventName
                v-if="itemCase.eventType === 'Пакет' && itemCase.participants?.team"
                :package-event="itemCase"
              />
              <ITeams
                v-else-if="itemCase.participants?.team"
                :first-team="itemCase.participants.team.homeTeam"
                :second-team="itemCase.participants.team.guestTeam"
                :name="itemCase.name"
                :is-home-logo-with-opponent="itemCase.isHomeLogoWithOpponent"
                :is-home-logo-without-opponent="itemCase?.isHomeLogoWithoutOpponent"
                :is-show-tournament="itemCase?.isShowTournament"
              />
              <div v-else class="name">
                <div class="name__img">
                  <img
                    :src="itemCase.participants?.singular.imageUrl"
                    alt="eventImage"
                  >
                </div>
                <div class="name__title">
                  {{ itemCase.participants?.singular.name }}
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              class="i-order-event-item__name"
              :class="{'i-order-event-item__name_service' : itemCase.type === 'service_simple'}"
            >
              <div class="name">
                <div class="name__title">{{ itemCase.name }}</div>
              </div>
            </div>
          </template>
        </div>
      </a>
      <button
        v-if="itemCase.eventType === 'Пакет'"
        class="i-order-event-item__show-packages-btn"
        :class="{ 'i-order-event-item__show-packages-btn_active' : isPackageEventsShow }"
        @click="isPackageEventsShow = !isPackageEventsShow"
      >
        <svg height="24" width="24">
          <use xlink:href="#chevron-down" />
        </svg>
      </button>
    </div>
    <div
      class="i-order-event-item__top-right"
      :class="isHistory ? 'i-order-event-item__history-details' : ''"
    >
      <div class="d-flex">
        <div v-if="itemCase.eventType !== 'Пакет'" class="i-order-event-item__details">
          <span
            v-if="itemCase.startTimestampStr && (itemCase.isShowDate || itemCase.isShowTime)"
            class="time"
          >
            <svg>
              <use xlink:href="#calendar" />
            </svg>
            <span v-if="itemCase.isShowDate">
              {{ dayjs(itemCase.startTimestampStr).format("DD MMMM YYYY") }}
            </span>
            <template v-if="itemCase.isShowTime">
              <span v-if="lang==='ru'" class="text-capitalize">
                &nbsp;{{ dayjs(itemCase.startTimestampStr).format("dd HH:mm") }}
              </span>
              <span v-else-if="lang==='en'" class="text-capitalize">
                &nbsp;{{ dayjs(itemCase.startTimestampStr).format("dd h:mm A") }}
              </span>
            </template>
          </span>
          <span class="place">
            <svg>
              <use xlink:href="#location" />
            </svg>
            {{ itemCase.unitName }}
          </span>
        </div>
        <div v-if="!isHistory" class="i-order-event-item__summary-price tablet-and-desktop">
          {{ translator.t('main', 'Price') }}: <span>{{ utils.formatNumber(itemCase.summaryPrice) }} ₽</span>
        </div>
      </div>
      <ILink
        v-if="trashButton"
        :icon-name="'trash'"
        class="desktop trash-btn"
        @click="
          removeEvent(
            itemCase.id,
            type ?? itemCase.type,
            ticketsCount ?? itemCase.tickets.length
          )
        "
      >
        {{ translator.t('main', 'Remove') }}
      </ILink>
      <ILink
        v-if="trashButton"
        :icon-name="'close'"
        class="mobile close-btn"
        @click="removeEvent(itemCase.id)"
      />
    </div>
    <div
      v-if="itemCase?.packageEvents?.length"
      v-show="isPackageEventsShow"
      class="i-order-event-item__package-events"
    >
      <IPackageEvent
        v-for="packageEvent in itemCase.packageEvents"
        :key="packageEvent.startTimestamp" :package-event="packageEvent"
      />
    </div>
    <div class="i-order-event-item__tickets">
      <slot>
        <div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
          <i-ticket
            :ticket="ticket"
            :is-history="isHistory"
            :inactive="false"
            :short="false"
            :allowed-loyalty="itemCase.allowedLoyaltyCategories"
            :event-type="itemCase.eventType"
            @remove-ticket="
              curTicket => removeTicket(curTicket, itemCase.id, itemCase.type)
            "
            @change-loyalty="
              ({ ticket, categoryId, priceId }) =>
                changeLoyalty(ticket, itemCase.id, categoryId, priceId)
            "
          >
            <ExtraInfo
              v-if="ticket.isEnableExtraInfo || itemCase?.eventType === 'Парковка'"
              :key="ticket.uid"
              :ticket="ticket"
              :is-history="isHistory"
            />
          </i-ticket>
        </div>
      </slot>
    </div>
    <div v-if="!isHistory" class="i-order-event-item__summary-price mobile">
      {{ translator.t('main', 'Price') }}: <span>{{ utils.formatNumber(itemCase.summaryPrice) }} ₽</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.i-order-event-item {
  background-color: $white;
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 20px;
  border: 1px solid $light-gray;
  border-radius: 16px;
  width: 100%;

  &__sub-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;

    margin-bottom: 16px;
  }

  &+.i-order-event-item {
    margin-top: 20px;
  }

  &__top {
    display: flex;
    justify-content: space-between;
  }

  &__top-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;
    margin-top: 14px;
  }

  .trash-btn {
    color: $error;
    margin-top: 5px;
    text-transform: uppercase;
    font-weight: 700;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__name {
    width: fit-content;

    .name {
      display: flex;
      align-items: center;
      margin-bottom: 20px;

      &__img {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 50px;
        height: 50px;
        min-width: 50px;
        min-height: 50px;
        margin-right: 10px;

        img {
          object-fit: contain;
          width: 100%;
          height: 100%;
        }
      }

      &__title {
        font-weight: 700;
        font-size: 16px;
        color: $black;
      }
    }
  }

  &__summary-price {
    span {
      font-weight: 700;
      font-size: 16px;
      font-family: 'Roboto', sans-serif, Arial;
    }
  }

  &__history {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  &__history-details {
    margin-bottom: 20px;
    margin-top: 0;
  }

  &__details {
    display: flex;
    align-items: flex-start;

    .time,
    .place {
      display: flex;
      align-items: center;
      font-weight: 400;

      svg {
        width: 16px;
        height: 16px;
        min-width: 16px;
        margin-right: 8px;
        margin-top: -1px;
      }
    }

    .time {
      margin-right: 24px;
      color: $primary;
      font-size: 16px;
    }

    .place {
      font-size: 12px;
      margin-right: 40px;
      max-width: 200px;
    }
  }

  &__tickets {
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    .tickets-item {
      margin-right: 16px;
      margin-bottom: 8px;
    }
  }

  &__top-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__package-events {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    width: 90%;
  }

  &__top-wrapper {
    display: flex;
    align-items: baseline;
    margin-bottom: 20px;
  }

  &__show-packages-btn {
    margin-left: 20px;
    padding: 5px 5px 5px 5px;
    transition: transform .3s ease;
  }

  &__show-packages-btn_active {
    transform: rotate(180deg);
  }

  @media (max-width: $screen-md) {
    padding: 24px 16px;

    &__top {
      flex-direction: column;

      &-left {
        order: 1;
      }

      &-right {
        align-items: flex-start;
        margin-top: 0;
      }
    }

    &__name {
      .name {
        margin-bottom: 8px;
      }
    }

    &__history-details {
      margin-bottom: 0;
    }

    &__details {
      margin-bottom: 20px;

      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .close-btn {
      position: absolute;
      top: 8px;
      right: 0;
    }

    &__summary-price {
      font-size: 14px;
      font-family: 'Roboto', sans-serif, Arial;

      span {
        font-weight: 700;
        font-size: 16px;
      }
    }

    &__package-events {
      width: 100%;
      gap: 16px;
    }
  }

  @media (max-width: $screen-small) {
    width: 100%;
    flex-direction: column;

    &__tickets {
      margin-bottom: 12px;

      .tickets-item {
        margin-right: 0px;
        width: 100%;

        &:not(&:last-of-type) {
          margin-bottom: 20px;
        }
      }
    }

    &__sub-wrapper {
      gap: 10px;
    }

    &__team-link {
      display: flex;
      width: 100%;
    }

    &__top-left {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &__name {
      align-self: center;

      &_service {
        align-self: flex-start;
      }
    }

    &__details {
      .time {
        font-size: 14px;
      }
    }

    &__top-wrapper {
      margin-bottom: 17.5px;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
  overflow: hidden;
  height: 100%;
}

.fade-enter-from,
.fade-leave-to {
  overflow: hidden;
  height: 0;
}

.history {
  font-size: 12px;
}
</style>
